import React, {useState} from 'react';

import {
  Text,
  Button,
  TextField,
  PasswordField,
  Anchor,
  addToast,
  addGenericErrorToast,
  Logo
} from '@albert-io/atomic';
import notifier from '@albert-io/notifier';
import {getOrigin, getMandarkURL, getHost} from '@albert-io/environment';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import FormDivider from 'client/components/Forms/FormDivider/FormDivider';
import {refreshSession, login} from '@albert-io/mandark/authentication';
import {redirect} from 'client/User/UserRedirectUtil';
import zuriSmile from 'assets/images/zuri/zuri_smile.svg';

import cleverIcon from '../SignUpForm/SocialButton/Clever/assets/clever_brand.svg';
import googleIcon from '../SignUpForm/SocialButton/Google/assets/google_light.svg';

import TermsFooter from './TermsFooter';
import {signUpFormSchema, type SignUpFormValues} from './signUpForm.schema';
import {useCreateUserMutation} from './signUp.queries.generated';
import {usePerformSignupStep} from './usePerformSignupStep';

interface SignUpFormStepProps {
  accountType: 'educator' | 'student';
}

export default function SignUpFormStep({accountType}: SignUpFormStepProps) {
  const isEducator = accountType === 'educator';
  const accountLabel = isEducator ? 'Educator' : 'Student';
  const [isSubmitting, setIsSubmitting] = useState(false);

  const googleAuthUrl = getMandarkURL(
    `/auth/google?${new URLSearchParams({
      account_type: accountType,
      redirect_host: getOrigin()
    })}`
  );
  const cleverAuthUrl = `${getHost()}/auth/clever`;

  const [createUser] = useCreateUserMutation();
  const performSignupStep = usePerformSignupStep();

  const {
    register,
    handleSubmit,
    watch,
    formState: {errors},
    setError
  } = useForm<SignUpFormValues>({
    defaultValues: {email: '', password: ''},
    resolver: zodResolver(signUpFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const onSubmit = async (data: SignUpFormValues) => {
    setIsSubmitting(true);

    try {
      const tempUsername = crypto.randomUUID().slice(0, 15);

      const {data: createData} = await createUser({
        variables: {
          input: {
            email: data.email,
            username: tempUsername,
            password: data.password,
            signUpMethod: 'email'
          }
        }
      });

      const createErrors = createData?.createUser?.errors ?? [];
      if (createErrors.length > 0) {
        const emailError = createErrors.find((e) => e.fields?.includes('email'));
        if (emailError) {
          setError('email', {message: 'This email is already in use.'});
          return;
        }
        throw new Error(createErrors[0].message || 'Failed to create account');
      }

      await login({username: tempUsername, password: data.password}, false);

      await performSignupStep({accountTypeSelection: {accountType}});

      addToast({
        message: "We've sent you a confirmation to the email you entered.",
        title: 'Your account is registered successfully!',
        color: 'positive'
      });

      await refreshSession();
      redirect();
    } catch (e) {
      notifier.notify(e instanceof Error ? e : new Error('Failed to create account'), {
        tags: 'SignUpFormStep'
      });
      addGenericErrorToast();
    } finally {
      setIsSubmitting(false);
    }
  };

  const emailField = register('email');
  const passwordField = register('password');
  const emailValue = watch('email');
  const passwordValue = watch('password');
  const hasErrors = !!errors.email || !!errors.password;
  const isEmpty = !emailValue || !passwordValue;

  return (
    <div className='flex max-w-[432px] flex-col gap-x5'>
      <div className='flex w-full flex-col items-center gap-x2'>
        <div className='m:hidden'>
          {isEducator ? (
            <Logo display='face' width='64px' height='64px' />
          ) : (
            <img src={zuriSmile} alt='' aria-hidden='true' className='h-x8 w-x8' />
          )}
        </div>
        <div className='flex flex-col gap-x0-half text-center'>
          <Text className='text-xxl font-black'>
            Sign Up as {isEducator ? 'an' : 'a'} {accountLabel}
          </Text>
        </div>
      </div>

      {/* SSO + Form */}
      <div className='flex w-full flex-col gap-x3'>
        <div className='flex w-full flex-col gap-x1'>
          <Button
            as='a'
            href={googleAuthUrl}
            color='secondary'
            variant='outlined'
            size='l'
            roundness='pill'
            className='w-full'
          >
            <img src={googleIcon} alt='' className='mr-x1-quarter h-x2 w-x2' />
            <Text color='secondary' bold>
              Continue with Google
            </Text>
          </Button>
          <Button
            as='a'
            href={cleverAuthUrl}
            color='secondary'
            variant='outlined'
            size='l'
            roundness='pill'
            className='w-full'
          >
            <img src={cleverIcon} alt='' className='mr-x1-quarter h-x2 w-x2' />
            <Text color='secondary' bold>
              Continue with Clever
            </Text>
          </Button>
        </div>
        <FormDivider>or</FormDivider>
        {/* Email/Password Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='flex w-full flex-col gap-x5'>
          <div className='flex flex-col gap-x1-half'>
            <TextField
              label='Email'
              type='email'
              placeholder='e.g. albert@gmail.com'
              maxLength={128}
              error={!!errors.email}
              message={errors.email?.message}
              {...emailField}
            />
            <PasswordField
              label='Password'
              error={!!errors.password}
              message={errors.password?.message}
              {...passwordField}
            />
          </div>
          <div className='flex w-full flex-col gap-x1-half'>
            <Button
              type='submit'
              className='w-full'
              roundness='pill'
              size='l'
              disabled={hasErrors || isEmpty || isSubmitting}
            >
              {isSubmitting ? 'Signing up...' : 'Sign up'}
            </Button>
            <Text className='text-center'>
              Already have an account?{' '}
              <Anchor to='/log-in' className='content-brand'>
                Log in
              </Anchor>
            </Text>
          </div>
        </form>
      </div>

      <TermsFooter />
    </div>
  );
}
